import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { caseService } from '@services/api/caseService';
import { useAuthStore } from '@stores/authStore';
import { getCaseStatusLabel, getCasePriorityLabel, getCaseTypeLabel, calculateSLAStatus } from '@/types/case';
import './CaseDetail.css';

export const CaseDetail = () => {
  const { id } = useParams();
  const { user } = useAuthStore();
  const [caseData, setCaseData] = useState<any>(null);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadCase();
  }, [id]);

  const loadCase = async () => {
    if (!id) return;
    const result = await caseService.getCaseById(id);
    if (result.success && result.data) {
      setCaseData(result.data as any);
    }
    setLoading(false);
  };

  const handleAddNote = async () => {
    if (!id || !user || !newNote.trim()) return;
    
    const result = await caseService.addCaseNote(id, newNote, user.id);
    if (result.success) {
      setNewNote('');
      loadCase(); // Reload to show new note
    }
  };

  const handleCloseCase = async () => {
    if (!id || !user) return;
    const resolution = prompt('Enter resolution details:');
    if (!resolution) return;
    
    const result = await caseService.closeCase(id, resolution, user.id);
    if (result.success) {
      loadCase();
    }
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;
  if (!caseData) return <div>Case not found</div>;

  const slaStatus = calculateSLAStatus(caseData.created_at, caseData.sla_due_at, caseData.status);

  return (
    <div className="case-detail">
      <Link to="/cases" className="customer-back-button">← Back to Cases</Link>
      
      <div className="case-detail-header">
        <div>
          <h1 className="page-title">Case #{caseData.id.slice(0, 8)}</h1>
          <p className="page-subtitle">{getCaseTypeLabel(caseData.case_type)}</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
          <span className={`priority-badge priority-${getCasePriorityLabel(caseData.priority).toLowerCase()}`}>
            {getCasePriorityLabel(caseData.priority)} Priority
          </span>
          <span className={`status-badge status-${getCaseStatusLabel(caseData.status).toLowerCase()}`}>
            {getCaseStatusLabel(caseData.status)}
          </span>
        </div>
      </div>

      <div className="case-detail-grid">
        <div className="case-detail-main">
          <div className="info-card">
            <h3 className="info-card-title">Case Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Created</span>
                <span className="info-value">{new Date(caseData.created_at).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Assigned To</span>
                <span className="info-value">
                  {caseData.assigned_to_staff ? 
                    `${caseData.assigned_to_staff.first_name} ${caseData.assigned_to_staff.last_name}` : 
                    'Unassigned'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">SLA Due</span>
                <span className={`info-value color-${slaStatus.status === 'overdue' ? 'red' : 'default'}`}>
                  {caseData.sla_due_at ? new Date(caseData.sla_due_at).toLocaleString() : 'N/A'}
                </span>
              </div>
              {caseData.customer && (
                <div className="info-item">
                  <span className="info-label">Customer</span>
                  <span className="info-value">
                    <Link to={`/customers/${caseData.customer.id}`} className="customer-name-link">
                      {caseData.customer.first_name} {caseData.customer.last_name}
                    </Link>
                  </span>
                </div>
              )}
            </div>

            {caseData.customer_message && (
              <div style={{ marginTop: 'var(--spacing-lg)', padding: 'var(--spacing-lg)', background: 'var(--color-ui-input-background)', borderRadius: 'var(--radius-xs)' }}>
                <p className="text-small" style={{ margin: 0 }}>{caseData.customer_message}</p>
              </div>
            )}
          </div>

          <div className="info-card">
            <h3 className="info-card-title">Activity Log & Notes</h3>
            
            {caseData.notes && caseData.notes.length > 0 && (
              <div className="notes-list">
                {caseData.notes.map((note: any) => (
                  <div key={note.id} className="note-item">
                    <div className="note-header">
                      <span className="note-author">
                        {note.staff_user?.first_name} {note.staff_user?.last_name}
                      </span>
                      <span className="note-time">{new Date(note.created_at).toLocaleString()}</span>
                    </div>
                    <p className="note-text">{note.note_text}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="add-note-section">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add internal note..."
                className="note-textarea"
                rows={3}
              />
              <button 
                onClick={handleAddNote}
                className="btn btn-primary btn-small"
                disabled={!newNote.trim()}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>

        <div className="case-detail-sidebar">
          <div className="info-card">
            <h3 className="info-card-title">Actions</h3>
            <div className="quick-actions">
              {caseData.status !== 'closed' && (
                <button onClick={handleCloseCase} className="btn btn-primary btn-small">
                  Close Case
                </button>
              )}
              <button className="btn btn-secondary btn-small">Reassign</button>
              <button className="btn btn-secondary btn-small">Send SMS</button>
              <button className="btn btn-ghost btn-small">Export Details</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

